/**
 * Hook for calculating zone heatmap occupancy data
 */

import { useMemo } from "react";
import { PlacementWithLocation } from "../../../../types";
import { Shape } from "../../../Settings/RampsSettings/RampEditor/RampCanvas/RampCanvas";
import { HEATMAP_HORIZON_HOURS } from "../heatmapConfig";
import { findSpotForPosition } from "../positionMapping";
import { assignSpotsToZones } from "../zoneHelpers";

export interface SpotOccupancyData {
  spotId: string;
  occupancyPercent: number;
  occupiedMinutes: number;
  availableMinutes: number;
}

export interface ZoneOccupancyData {
  zoneId: string;
  zoneName: string;
  occupancyPercent: number;
  totalSpots: number;
  occupiedSpots: number;
}

/**
 * Calculate heatmap occupancy data from placements
 *
 * @param placements - Placements within the heatmap time window
 * @param shapes - Ramp shapes for position→spot mapping
 * @param startDate - Start of heatmap window
 * @param rampDepth - Ramp depth in feet (for Y-axis coordinate flip)
 * @param horizonHours - Time horizon in hours (overrides default)
 * @returns Map of spotId → occupancy data
 */
export function calculateHeatmapOccupancy(
  placements: PlacementWithLocation[],
  shapes: Shape[],
  startDate: Date,
  rampDepth: number,
  horizonHours: number = HEATMAP_HORIZON_HOURS
): Map<string, SpotOccupancyData> {
  const horizonMinutes = horizonHours * 60;
  const endDate = new Date(startDate.getTime() + horizonHours * 60 * 60 * 1000);
  const startTime = startDate.getTime();
  const endTime = endDate.getTime();

  // Map to accumulate occupied minutes per spot
  const spotOccupancyMap = new Map<string, number>();

  for (const placement of placements) {
    // Skip placements without positions
    if (
      placement.x === null ||
      placement.x === undefined ||
      placement.y === null ||
      placement.y === undefined
    ) {
      continue;
    }

    // Find which spot this placement occupies
    // NOTE: findSpotForPosition handles Y-axis flipping internally
    // Placements use database coordinates (origin bottom-left, Y↑)
    // Shapes use SVG coordinates (origin top-left, Y↓)
    const spotId = findSpotForPosition(
      {
        x: placement.x,
        y: placement.y, // Pass directly - findSpotForPosition handles Y-flip
      },
      shapes,
      rampDepth
    );

    if (!spotId) {
      // Position not in any spot - skip
      continue;
    }

    // Calculate overlap between placement time range and heatmap window
    const placementStart = new Date(placement.start_time).getTime();
    const placementEnd = new Date(placement.end_time).getTime();

    const overlapStart = Math.max(placementStart, startTime);
    const overlapEnd = Math.min(placementEnd, endTime);

    if (overlapStart >= overlapEnd) {
      // No overlap with heatmap window
      continue;
    }

    const overlapMinutes = (overlapEnd - overlapStart) / (1000 * 60);

    // Accumulate occupied time for this spot
    const currentOccupancy = spotOccupancyMap.get(spotId) || 0;
    spotOccupancyMap.set(spotId, currentOccupancy + overlapMinutes);
  }

  // Convert to final data structure with normalized percentages
  const result = new Map<string, SpotOccupancyData>();

  // Convert Map entries to array to avoid downlevelIteration requirement
  Array.from(spotOccupancyMap.entries()).forEach(
    ([spotId, occupiedMinutes]) => {
      // Cap at 100% (in case of overlapping placements or data issues)
      const cappedMinutes = Math.min(occupiedMinutes, horizonMinutes);
      const occupancyPercent = (cappedMinutes / horizonMinutes) * 100;
      const availableMinutes = horizonMinutes - cappedMinutes;

      result.set(spotId, {
        spotId,
        occupancyPercent,
        occupiedMinutes: cappedMinutes,
        availableMinutes: Math.max(0, availableMinutes),
      });
    }
  );

  return result;
}

/**
 * Calculate zone-level occupancy by aggregating spot occupancy
 *
 * @param spotOccupancy - Map of spot occupancy data
 * @param shapes - Ramp shapes (includes zones and spots)
 * @returns Map of zoneId → aggregated occupancy data
 */
export function calculateZoneOccupancy(
  spotOccupancy: Map<string, SpotOccupancyData>,
  shapes: Shape[]
): Map<string, ZoneOccupancyData> {
  const spotShapes = shapes.filter((s) => s.type === "Spot");
  const zoneShapes = shapes.filter((s) => s.type === "Zone");

  // Assign spots to zones using existing helper
  const zoneToSpots = assignSpotsToZones(spotShapes, zoneShapes);

  const result = new Map<string, ZoneOccupancyData>();

  for (const zoneShape of zoneShapes) {
    const spotIds = zoneToSpots.get(zoneShape.id) || [];

    if (spotIds.length === 0) {
      // Zone with no spots - show as 0% occupancy (baseline green)
      result.set(zoneShape.id, {
        zoneId: zoneShape.id,
        zoneName: zoneShape.text || zoneShape.id,
        occupancyPercent: 0,
        totalSpots: 0,
        occupiedSpots: 0,
      });
      continue;
    }

    // Calculate average occupancy across all spots in zone
    let totalOccupancy = 0;
    let occupiedSpotsCount = 0;

    for (const spotId of spotIds) {
      const spotData = spotOccupancy.get(spotId);
      if (spotData) {
        totalOccupancy += spotData.occupancyPercent;
        if (spotData.occupancyPercent > 0) {
          occupiedSpotsCount++;
        }
      }
    }

    const avgOccupancy = totalOccupancy / spotIds.length;

    result.set(zoneShape.id, {
      zoneId: zoneShape.id,
      zoneName: zoneShape.text || zoneShape.id,
      occupancyPercent: avgOccupancy,
      totalSpots: spotIds.length,
      occupiedSpots: occupiedSpotsCount,
    });
  }

  return result;
}

/**
 * Hook to calculate and memoize spot-based heatmap occupancy data
 *
 * @param placements - Placements to analyze
 * @param shapes - Ramp shapes for position→spot mapping
 * @param selectedDate - Start of heatmap window
 * @param rampDepth - Ramp depth in feet (for Y-axis coordinate flip)
 * @param horizonHours - Time horizon in hours (defaults to HEATMAP_HORIZON_HOURS)
 * @returns Memoized map of spotId → occupancy data
 */
export function useHeatmapData(
  placements: PlacementWithLocation[],
  shapes: Shape[],
  selectedDate: Date,
  rampDepth: number,
  horizonHours?: number
): Map<string, SpotOccupancyData> {
  return useMemo(() => {
    return calculateHeatmapOccupancy(
      placements,
      shapes,
      selectedDate,
      rampDepth,
      horizonHours
    );
  }, [placements, shapes, selectedDate, rampDepth, horizonHours]);
}
